import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Paper, Stack, Tab } from "@mui/material";
import { type FC, type SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ChannelName,
  isChatChannel,
  isEmailChannel,
} from "../../../../modules/channels/types";
import {
  type GroupedMomentAnalyticsData,
  type SentMomentData,
} from "../../../../modules/stats/server/analytics-service";
import type { UUID } from "../../../../utils/uuid";
import { AnalyticsSendsTableExpandedDeliveries } from "./expanded-delivered";
import { AnalyticsSendsTableExpandedInteractions } from "./expanded-interactions/expanded-interactions";
import { AnalyticsSendsTableExpandedIssues } from "./expanded-issues";
import { AnalyticsSendsTableExpandedOpens } from "./expanded-opens";
import { AnalyticsSendsTableExpandedPollsAndSurveys } from "./expanded-polls_and_surveys";
import { AnalyticsSendsTableExpandedReactions } from "./expanded-reactions/expanded-reactions";

type Tabs = "delivered" | "opens" | "interactions" | "issues" | "reactions" | "polls_and_surveys";

export const AnalyticsSendsTableExpandedRow: FC<{
  moment_id: UUID;
  channel: ChannelName;
  stats: GroupedMomentAnalyticsData["stats"];
  has_dynamic_content: boolean;
  polls_and_surveys?: {
    poll_ids: UUID[];
    survey_data: SentMomentData["survey_data"];
  };
  to_channel?: boolean;
}> = ({
  moment_id,
  channel,
  stats,
  has_dynamic_content,
  polls_and_surveys,
  to_channel = false,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tabs>("delivered");

  const usePollsAndSurveys = useMemo(
    () =>
      polls_and_surveys !== undefined &&
      ((polls_and_surveys?.poll_ids ?? []).length > 0 ||
        (polls_and_surveys?.survey_data ?? []).length > 0),
    [polls_and_surveys],
  );

  const handleChange = useCallback(
    (_: SyntheticEvent, newValue: Tabs) => {
      if (!isChatChannel(channel) && newValue === "reactions") return;

      if (!isEmailChannel(channel) && newValue === "opens") return;

      if (!usePollsAndSurveys && newValue === "polls_and_surveys") return;

      setTab(newValue);
    },
    [channel, usePollsAndSurveys],
  );

  return (
    <Stack sx={{ px: 2, height: "100%", boxSizing: "border-box" }} direction="column">
      <TabContext value={tab}>
        <Stack direction="column" spacing={2} sx={{ my: 2 }}>
          <TabList onChange={handleChange}>
            <Tab label={t("Delivered")} value="delivered" />
            {isEmailChannel(channel) && <Tab label={t("Unique Opens")} value="opens" />}
            <Tab label={t("Interacted")} value="interactions" />
            {isChatChannel(channel) && <Tab label={t("Reactions")} value="reactions" />}
            <Tab label={t("Issues")} value="issues" />
            {usePollsAndSurveys && <Tab label={t("Surveys and polls")} value="polls_and_surveys" />}
          </TabList>
          <Paper elevation={6} sx={{ px: 3, py: 2 }}>
            <TabPanel value="delivered">
              <AnalyticsSendsTableExpandedDeliveries
                stats={stats}
                switchToIssuesTab={() => setTab("issues")}
              />
            </TabPanel>
            {isEmailChannel(channel) && (
              <TabPanel value="opens">
                <AnalyticsSendsTableExpandedOpens stats={stats} />
              </TabPanel>
            )}
            <TabPanel value="interactions">
              <AnalyticsSendsTableExpandedInteractions
                stats={stats}
                has_dynamic_content={has_dynamic_content}
                to_channel={to_channel}
              />
            </TabPanel>
            {isChatChannel(channel) && (
              <TabPanel value="reactions">
                <AnalyticsSendsTableExpandedReactions stats={stats} />
              </TabPanel>
            )}
            <TabPanel value="issues">
              <AnalyticsSendsTableExpandedIssues stats={stats} />
            </TabPanel>
            {usePollsAndSurveys && (
              <TabPanel value="polls_and_surveys">
                <AnalyticsSendsTableExpandedPollsAndSurveys
                  moment_id={moment_id}
                  data={polls_and_surveys!}
                />
              </TabPanel>
            )}
          </Paper>
        </Stack>
      </TabContext>
    </Stack>
  );
};
